import { useState } from "react";
import { Star, Banknote, Repeat } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export function BookAgain({ booking }) {
  return (
    <Link to={`/book?service=${booking.service_id}&location=${booking.location_id}`} data-testid={`book-again-${booking.booking_id}`}
      className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-purple-200 text-purple-700 text-xs hover:bg-lilac-mist transition-colors">
      <Repeat size={13} /> Book this again
    </Link>
  );
}

export function ReviewForm({ booking, existing, onDone }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  if (existing) {
    return (
      <span className="flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-slate-400" data-testid={`review-done-${booking.booking_id}`}>
        {Array.from({ length: existing.rating }).map((_, i) => <Star key={i} size={11} className="text-gold" fill="currentColor" />)} Thanks for your review
      </span>
    );
  }
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid={`review-open-${booking.booking_id}`}
        className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-obsidian text-white text-xs hover:text-gold transition-colors">
        <Star size={13} /> Leave a review
      </button>
    );
  }
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reviews", { booking_id: booking.booking_id, rating, text });
      toast.success("Thank you! Empress will feature the best reviews on the site.");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="w-full mt-2 rounded-2xl bg-lilac-mist p-4 space-y-3" data-testid={`review-form-${booking.booking_id}`}>
      <div className="flex items-center gap-1" data-testid="review-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => setRating(n)} data-testid={`review-star-${n}`} aria-label={`${n} stars`}>
            <Star size={20} className={n <= rating ? "text-gold" : "text-purple-200"} fill={n <= rating ? "currentColor" : "none"} />
          </button>
        ))}
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={400} placeholder="How was your session with Empress?" data-testid="review-text"
        className="w-full rounded-xl border border-purple-100 px-3 py-2 text-sm bg-white" />
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy || text.trim().length < 10} data-testid="review-submit" className="px-5 py-2 rounded-full bg-gold text-obsidian text-xs font-semibold disabled:opacity-50">Post review</button>
        <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-xs text-slate-500">Cancel</button>
      </div>
    </div>
  );
}

export function CashOption({ booking, onDone }) {
  const [open, setOpen] = useState(false);
  const request = async () => {
    try {
      await api.post(`/bookings/${booking.booking_id}/deposit/cash`);
      toast.success("Empress has been notified. Your time locks once she confirms.");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid={`cash-open-${booking.booking_id}`} className="text-[11px] text-slate-400 underline underline-offset-4 hover:text-purple-700 transition-colors">
        Already arranged cash with Empress?
      </button>
    );
  }
  return (
    <div className="w-full mt-2 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid={`cash-dialog-${booking.booking_id}`}>
      <p className="flex items-center gap-2 font-medium text-obsidian text-sm"><Banknote size={16} className="text-amber-600" /> Paying your deposit in cash</p>
      <p className="text-xs text-slate-600 mt-2 leading-relaxed">
        Choose this <span className="font-semibold">only if you have already agreed with Empress</span> to bring ${booking.deposit_amount} in cash. Your time is <span className="font-semibold">not locked</span> until she confirms, and unconfirmed sessions can be released to other artists. A card deposit locks it instantly.
      </p>
      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={() => setOpen(false)} data-testid={`cash-back-${booking.booking_id}`} className="px-5 py-2 rounded-full bg-gold text-obsidian text-xs font-semibold">Pay by card instead</button>
        <button onClick={request} data-testid={`cash-confirm-${booking.booking_id}`} className="px-5 py-2 rounded-full border border-amber-300 text-amber-800 text-xs hover:bg-amber-100 transition-colors">Yes, Empress agreed to cash</button>
      </div>
    </div>
  );
}
